import { ConsignesCompartment } from "@/src/app/hospitalisation/consignesCompartment";
import { HospitalisationEventsCompartment } from "@/src/app/hospitalisation/hospitalisationEvents";
import { HospitalisationTreatment } from "@/src/app/hospitalisation/hospitalisationTreatment";

import {
  Appointment,
  CardItemType,
  Hospitalisation,
  Patient,
  protocolTypes,
} from "@/src/data/allTypes";
import { getItemId, getOptionsFromRecord, uid } from "@/src/utils/utils";
import moment from "moment";
import { useState } from "react";
import { BiInjection, BiTestTube } from "react-icons/bi";
import { TbReportMedical } from "react-icons/tb";

import { CompartmentCard } from "@/src/components/cards";
import { IconTab } from "@/src/components/iconTab";
import { Input } from "@/src/components/universalInput";
import { DocumentsCompartment } from "../documents/documentsCompartment";
import { DiagnosticsCompartment } from "../hospitalisation/diagnosticsCompartment";
import { HospitalisationExamCompartments } from "../hospitalisation/hospitalisationExamsCompartment";
import { MesuresAndScoresCompartment } from "../hospitalisation/mesuresCompartment";
import { OrdonnancesCompartment } from "../hospitalisation/ordonnancesCompartment";
import { StoryCompartment } from "../hospitalisation/storyCompartment";
import { AntecedentsCompartment } from "../patients/antecedentsHistory";

function CurePage({
  item,
  appointment,
  patient,
  onChange,
  onAppointmentChange,
  onImmediateDischarge,
}: {
  item: Hospitalisation;
  appointment: Appointment;
  patient: Patient;
  onChange?: (item: Hospitalisation) => void;
  onAppointmentChange?: (appointment: Appointment) => void;
  onImmediateDischarge: () => void;
}) {
  let [tabId, setTabId] = useState("hospitalisation");
  if (!appointment) return <div>Aucun Rendez-vous de cure trouvé</div>;

  const tabs: {
    id: CardItemType;
    name: string;
    icon: JSX.Element;
    content: JSX.Element;
  }[] = [
    {
      id: "hospitalisation",
      name: "Données cliniques",
      icon: <TbReportMedical size={20} />,
      content: (
        <div className="tabContent grid mc1 gap20">
          <div className="c1 gap10">
            <DiagnosticsCompartment
              parent={item}
              patient={patient}
            ></DiagnosticsCompartment>

            <StoryCompartment patient={patient} parentId={getItemId(item)} />
            <Input
              name={"Notes"}
              type={"textarea"}
              value={item?.notes}
              onChange={() => {}}
              onBlur={(newValue) => {
                onChange?.({ ...item, notes: newValue });
              }}
            />
          </div>

          <CureInformationCompartment
            currentAppointment={appointment}
            patient={patient}
            onChange={(newValue) => {
              // onAppointmentChange?.(newValue);
            }}
          />
          <AntecedentsCompartment patient={patient} />

          <HospitalisationEventsCompartment patient={patient} parent={item} />
          <MesuresAndScoresCompartment
            patient={patient}
            parent={item}
            color="simple"
          />
          {/* <ToxicityCompartment
            parent={item}
            patient={patient}
          ></ToxicityCompartment> */}
          <DocumentsCompartment
            patient={patient}
            parent={item}
            color="blue"
          ></DocumentsCompartment>
        </div>
      ),
    },
    {
      id: "exam",
      name: "Examens",
      icon: <BiTestTube size={18} />,
      content: (
        <div className="tabContent grid c1 gap20">
          <HospitalisationExamCompartments
            patient={patient}
            parent={item}
          ></HospitalisationExamCompartments>
        </div>
      ),
    },

    {
      id: "ordonnance",
      name: "Traitement",
      icon: <BiInjection size={18} />,
      content: (
        <div className="tabContent grid c5 gap20">
          <div className="triple">
            <HospitalisationTreatment
              templates={appointment.protocolDay?.templates}
              patient={patient}
              parent={item}
            ></HospitalisationTreatment>
          </div>
          <ConsignesCompartment
            patient={patient}
            parent={item}
          ></ConsignesCompartment>
          <OrdonnancesCompartment
            patient={patient}
            parent={item}
            onChange={(newValue) => {
              console.log({ newValue });
            }}
          ></OrdonnancesCompartment>
        </div>
      ),
    },
  ];

  const selectedTab = tabs.find((tab) => tab.id == tabId);

  return (
    <div className="tabsPage fullPage c1" style={{ gap: "20px" }}>
      {
        <div className="grid  gap10">
          {tabs.map((tab) => {
            return (
              <IconTab
                key={uid()}
                color={tab.id as any}
                activeTabId={tabId}
                id={tab.id}
                icon={tab.icon}
                name={tab.name}
                onChange={(tab) => {
                  setTabId(tab);
                }}
              />
            );
          })}
        </div>
      }

      {selectedTab?.content}
    </div>
  );
}

function CureInformationCompartment({
  patient,
  currentAppointment,
  onChange,
}: {
  patient: Patient;
  currentAppointment: Appointment;
  onChange?: (newValue: Appointment) => void;
}) {
  const [appointment, setAppointment] = useState(currentAppointment);

  function onValueChange(newValue: Appointment) {
    setAppointment(newValue);
    onChange?.(newValue);
  }

  return (
    <CompartmentCard title="Informations générales">
      <div className="c1 gap10">
        <div className="bold  ">{appointment?.protocol?.name}</div>
        <Input
          name={"Date de la cure"}
          type={"date"}
          value={moment(appointment?.start).format("YYYY-MM-DD")}
          onChange={() => {}}
          onBlur={(newValue) => {
            onValueChange({ ...appointment, start: newValue });
          }}
        />
        <Input
          name={"Type"}
          type={"select"}
          value={appointment?.protocolType}
          options={getOptionsFromRecord(protocolTypes)}
          onChange={(newValue) => {
            onValueChange({ ...appointment, protocolType: newValue });
          }}
        />

        <Input
          name={"Cure N°"}
          type={"select"}
          value={appointment?.protocolCycle}
          options={Array.from({ length: 60 }, (_, i) => i + 1).map((day) => {
            return { value: day?.toString(), label: `C${day}` };
          })}
          onChange={(newValue) => {
            onValueChange({ ...appointment, protocolCycle: newValue });
          }}
        />

        <Input
          name={"Jour N°"}
          type={"select"}
          value={appointment?.protocolDay?.day}
          options={
            appointment?.protocol?.days?.map((protocolDay) => {
              return {
                value: protocolDay?.day?.toString(),
                label: `J${protocolDay?.day}`,
              };
            }) || []
          }
          onChange={(newValue) => {
            onValueChange({
              ...appointment,
              protocolDay: appointment?.protocol?.days.find(
                (protocolDay) => protocolDay?.day == newValue
              ),
            });
          }}
        />
      </div>
    </CompartmentCard>
  );
}

export { CurePage };
